import {
  AUTOMATION_TRIGGER_OPTIONS,
  AUTOMATION_ACTION_OPTIONS,
} from "@/lib/const";
import { flattenTriggers } from "@/lib/flattenTriggers";
import { flattenActions } from "@/lib/flattenActions";
import { REMAPPABLE_DYNAMIC_OPTIONS } from "@/lib/automationExport";
import {
  ChainInfo,
  TRIGGER_CHAIN,
  ACTION_CHAIN,
} from "@/lib/automationChaining";
import { BuilderTrigger, BuilderAction } from "@/types/v2/Automation";
import { TOAST_FIELD_KEYS, TOAST_FIELD_SLOTS } from "@/lib/automationShowToast";

const isRemappable = (d: string | null | undefined): boolean =>
  !!d && (REMAPPABLE_DYNAMIC_OPTIONS as string[]).includes(d);

// Triggers: a child is an optional condition on its base trigger.
const triggerChainLines = (info: ChainInfo | undefined): string[] => {
  if (!info) return [];
  const lines: string[] = [];
  if (info.parentId)
    lines.push(
      `- chaining: a condition on ${info.parentId} ("${info.parentLabel}") — include ${info.parentId} as the base trigger in the same automation`
    );
  if (info.children?.length)
    lines.push(
      `- chaining: optional conditions you may add as separate triggers in the same automation: ${info.children
        .map((c) => `${c.id} ("${c.label}")`)
        .join(", ")}`
    );
  return lines;
};

// A promise-time top-level trigger (trigger-006/007/009) can only appear in
// an automation whose OTHER top-level triggers are also promise-time — the
// scheduled scan only evaluates that subset, so import rejects any mixed set
// (see validateAutomationForImport in automationImport.ts). Scoped to
// top-level triggers only (chain?.parentId unset): a condition child (e.g.
// trigger-006-1) already carries its own "chaining: a condition on
// trigger-006" note from triggerChainLines, so repeating the whole-automation
// constraint on all 24 condition entries would be pure repetition in the
// generated prompt.
//
// trigger-010 (promiseTimeChangedFromToday) is also an onPromiseTime
// top-level trigger, but is deliberately EXCLUDED from the
// trigger-006/007/009 combinable group: the reactive evaluator
// (evaluateChangedFromTodayTrigger) doesn't honor phase-group AND
// conjunction with sibling phase triggers the way the scanner's
// evaluateAutomationPhase does, so combining it with 006/007/009 would
// produce an automation whose reactive and scanner evaluation paths
// disagree. It gets its own constraint line spelling that out explicitly —
// generically reusing the 006/007/009 combinable text under trigger-010's
// own catalog entry would read as an implicit "I'm one of these too," which
// is exactly the inference this exclusion exists to prevent.
const triggerConstraintLines = (
  t: BuilderTrigger,
  chain?: ChainInfo
): string[] => {
  if (t.triggerEventType !== "onPromiseTime") return [];
  if (chain?.parentId) return [];
  if (t.triggerField === "promiseTimeChangedFromToday") {
    return [
      `- constraint: this trigger stands alone — do NOT combine it with trigger-006, trigger-007, or trigger-009 in the same automation. It may carry its own condition sub-triggers, and this automation's triggers array must contain ONLY this trigger and/or its own condition sub-triggers, never a trigger of a different triggerEventType.`,
    ];
  }
  return [
    `- constraint: promise-time top-level triggers (trigger-006, trigger-007, trigger-009) may be combined with each other AND with their own condition sub-triggers in the same automation — but this automation's triggers array must contain ONLY promise-time triggers and/or their own condition sub-triggers, never a trigger of a different triggerEventType.`,
  ];
};

// Actions: a child is a sub-action that configures its parent; some are REQUIRED.
const actionChainLines = (info: ChainInfo | undefined): string[] => {
  if (!info) return [];
  const lines: string[] = [];
  if (info.parentId)
    lines.push(
      `- chaining: ${info.required ? "REQUIRED" : "optional"} sub-action of ${info.parentId} ("${info.parentLabel}") — only valid alongside ${info.parentId}${
        info.required
          ? `; must be included whenever ${info.parentId} is used`
          : ""
      }`
    );
  if (info.children?.length)
    lines.push(
      `- chaining: add these sub-actions as separate entries in the same actions array: ${info.children
        .map(
          (c) =>
            `${c.id} ("${c.label}") [${c.required ? "REQUIRED" : "optional"}]`
        )
        .join(", ")}`
    );
  return lines;
};

// A field the template pins to one value renders as that value; a field the
// template offers as a list renders as "a | b | c"; a field pinned to null
// (or absent) renders as the literal JSON token "null".
const scalarOrList = (
  options: readonly string[] | null | undefined,
  scalar: string | null
): string => {
  if (options && options.length) return options.join(" | ");
  return scalar ?? "null";
};

const triggerValueRule = (t: BuilderTrigger): string => {
  if (isRemappable(t.triggerValueOptionsDynamic))
    return `SHOP VALUE (${t.triggerValueOptionsDynamic}) — emit a placeholder string AND a resolvedReferences entry`;
  if (t.triggerValueEditor === "schedule")
    return `a JSON object encoded as a STRING (not a bare object), e.g. triggerValue: "{\\"cadence\\":\\"weekdays\\",\\"days\\":[],\\"time\\":\\"08:00\\"}". cadence is "daily" | "weekdays" | "days"; days is an array of "sun".."sat" and is only read when cadence is "days"; time is 24-hour "HH:MM". Anything malformed is silently replaced with the default (daily at 08:00), so emit all three fields.`;
  if (t.triggerValueEditable)
    return `free value${t.triggerPlaceholder ? ` (e.g. "${t.triggerPlaceholder}")` : ""}`;
  return `fixed: ${JSON.stringify(t.triggerValue)}`;
};

const actionValueRule = (a: BuilderAction): string => {
  if (isRemappable(a.actionValueOptionsDynamic))
    return `SHOP VALUE (${a.actionValueOptionsDynamic}) — emit a placeholder string AND a resolvedReferences entry`;
  if (a.actionField === "showToast") {
    // The stored value is a JSON object encoded as a STRING, not a bare
    // object or a bare headline string — a bare string parses as invalid
    // JSON and silently falls back to a blank headline, which the backend
    // now refuses to write at all (see automations.ts), so a malformed
    // value produces no toast rather than a blank one.
    return (
      `a JSON object encoded as a STRING (not a bare object or a bare headline string), ` +
      `e.g. actionValue: "{\\"headline\\":\\"Promise time approaching\\",\\"fields\\":[\\"Technician\\",null,null]}". ` +
      `Shape: { headline: string, fields: (fieldKey|null)[${TOAST_FIELD_SLOTS}] }. ` +
      `headline must be a non-blank string. fields must have exactly ${TOAST_FIELD_SLOTS} entries, ` +
      `each either null (empty slot) or one of: ${TOAST_FIELD_KEYS.join(", ")}.`
    );
  }
  if (a.multiSelect) {
    // The stored value is a JSON array encoded as a STRING, not a bare array —
    // a bare array fails schema parse and rejects the whole file.
    const tagsNote =
      a.actionValueOptionsDynamic === "tags"
        ? ` Each entry must be a tag label that already exists in this shop — ask the user for their exact tag names (unknown tags are written to the card, not matched, and are NOT remapped on import).`
        : "";
    return `a JSON array encoded as a STRING (not a bare array), e.g. actionValue: "[\\"A\\",\\"B\\"]".${tagsNote}`;
  }
  if (a.actionValueEditable)
    return `free value${a.actionPlaceholder ? ` (e.g. "${a.actionPlaceholder}")` : ""}`;
  return `fixed: ${JSON.stringify(a.actionValue)}`;
};

const formatTrigger = (t: BuilderTrigger, chain?: ChainInfo): string =>
  [
    `### ${t.id} — "${t.buttonOptions.text}"`,
    `- triggerObject: card`,
    `- triggerEventType: ${scalarOrList(
      t.triggerEventTypeOptions,
      t.triggerEventType ?? null
    )}`,
    `- triggerField: ${scalarOrList(
      t.triggerFieldOptions ?? undefined,
      t.triggerField
    )}`,
    `- triggerFieldChange: ${scalarOrList(
      t.triggerFieldChangeOptions,
      t.triggerFieldChange
    )}`,
    `- triggerCondition: ${scalarOrList(
      t.triggerConditionOptions?.map((o) => o.value),
      t.triggerCondition
    )}`,
    `- triggerValue: ${triggerValueRule(t)}`,
    ...triggerChainLines(chain),
    ...triggerConstraintLines(t, chain),
  ].join("\n");

const formatAction = (a: BuilderAction, chain?: ChainInfo): string =>
  [
    `### ${a.id} — "${a.buttonOptions.text}"`,
    `- actionObject: card`,
    `- actionObjectIdentifier: self`,
    `- actionEventType: ${a.actionEventType}`,
    `- actionField: ${scalarOrList(
      a.actionFieldOptions?.map((o) => o.value),
      a.actionField
    )}`,
    `- actionValue: ${actionValueRule(a)}`,
    ...actionChainLines(chain),
  ].join("\n");

const HEADER = `# Shop Hero Automation Generator

You generate automation import files for the Shop Hero dashboard. When the user
describes what they want, respond with ONLY a JSON file matching the schema and
catalog below — no prose, no explanation, no code fences unless asked.

## Output format

\`\`\`json
{
  "schemaVersion": 1,
  "exportedAt": "<any ISO 8601 timestamp>",
  "sourceShopId": "llm-generated",
  "automations": [
    {
      "name": "<short name>",
      "description": "<what it does>",
      "enabled": true,
      "triggersConditional": "AND",
      "triggers": [
        {
          "templateId": "<from catalog>",
          "triggerObject": "card",
          "triggerEventType": "<from the template>",
          "triggerField": "<from the template, or null>",
          "triggerFieldChange": "<from the template, or null>",
          "triggerCondition": "<from the template, or null>",
          "triggerValue": "<per the template's triggerValue rule, or null>"
        }
      ],
      "actions": [
        {
          "templateId": "<from catalog>",
          "actionObject": "card",
          "actionObjectIdentifier": "self",
          "actionEventType": "<from the template>",
          "actionField": "<from the template, or null>",
          "actionValue": "<per the template's actionValue rule, or null>"
        }
      ],
      "resolvedReferences": []
    }
  ]
}
\`\`\`

## Rules

- Do NOT include an "id" field on any automation — every automation imports as new.
- Choose triggers and actions ONLY from the catalog below. Copy the templateId and
  each pinned field value exactly as shown; where a field lists "a | b | c", pick one.
- Include EVERY field shown for each trigger and action, even when the value is null —
  emit null explicitly, never omit a key (a missing key rejects the whole file).
- Emit each value with its JSON type: booleans as true/false (never the string "true"),
  and copy fixed values exactly as the template shows them.
- "triggersConditional" is "AND" (all triggers must hold) or "OR" (any one trigger).
  Prefer "AND": condition sub-triggers (see chaining) only make sense under "AND". Each
  trigger is evaluated independently, so "OR" cannot express "one composite condition OR
  another" — to offer alternatives like that, emit SEPARATE automations, one per
  alternative, not a single "OR" automation. Exception: promise-time triggers
  (trigger-006, trigger-007, trigger-009) may combine with EACH OTHER and with their own
  condition sub-triggers in the same automation, but can never be combined with a trigger
  of a different type — see their catalog entries. For a promise-time automation,
  "triggersConditional" does NOT apply to one flat OR/AND set the way it does above: it
  governs OR/AND independently WITHIN the phase-trigger group (whichever of trigger-006 /
  trigger-007 / trigger-009 are present) and WITHIN the condition-trigger group (their
  "And ..." sub-triggers) — but the automation only fires when BOTH groups are satisfied;
  phase due-ness and conditions are ALWAYS AND'd together as two separate groups,
  regardless of "triggersConditional". Example: [trigger-009, trigger-009-1 ("And is a
  waiter")] under "OR" still means "promise time is today AND is a waiter" — there is only
  one trigger in each group, so "OR within the group" is vacuous — it does NOT mean
  "either."
- SHOP VALUES (containers, technicians, serviceAdvisors, colors) are not known to you.
  Where a template's value rule says "SHOP VALUE", put a descriptive placeholder string
  (e.g. "PLACEHOLDER_INSPECTION_CONTAINER") as the value, AND add an entry to that
  automation's resolvedReferences:
    { "kind": "trigger" | "action",
      "index": <0-based position in that automation's triggers/actions array>,
      "templateId": "<same templateId>",
      "dynamicType": "containers" | "technicians" | "serviceAdvisors" | "colors",
      "value": "<the exact placeholder string>",
      "label": "<human description, e.g. the Inspection container>" }
  The user maps each placeholder to a real shop entity during import. An unmapped
  placeholder skips that automation, so give every SHOP VALUE a resolvedReferences entry.
- Loop safety: a state trigger — one whose "triggerFieldChange" is null — matches on
  EVERY card update while its condition holds, not just once. Never combine a state
  trigger with a timed sub-action ("For X minutes" / "After X minutes") or a timer
  action: those rewrite the card on every run and re-fire the automation endlessly. When
  the actions include a timed or timer action, drive the automation with a change-based
  trigger ("triggerFieldChange": "changedTo" or "changedFrom") instead.
- Scheduled automations sweep the BOARD, not one card. A "trigger-008" ("On a schedule")
  automation applies its actions to every open card matching its conditions at the
  scheduled time — with no condition sub-triggers, that is every open card on the board.
  Prefer adding at least one condition sub-trigger to target specific cards, and never
  pair "trigger-008" with "action-007" ("Change card color to"): it overwrites the card's
  real colour with no way to reset it, and the backend drops it on the scheduled path.
  Use "action-003" ("Set card color") instead — same result, and resettable.
- Chaining: some triggers and actions are chained — a base/parent entry plus sub-entries
  that refine or configure it (see each template's "chaining:" notes below). Add each as
  its own entry in the same array, parent first, then its sub-entries. A sub-entry is only
  valid alongside its parent; a sub-entry marked [REQUIRED] MUST be included whenever its
  parent is used (e.g. "Flash color" requires its color sub-action). Never emit a sub-entry
  without its parent, or a parent that is missing a [REQUIRED] sub-entry.
- Mutually exclusive fixed conditions: some condition sub-triggers pin OPPOSITE fixed
  values of the same field — e.g. "And is a waiter" (isWaiter: true) and "And is a
  drop-off" (isWaiter: false). Under "AND" these can never both hold, so never combine
  two such sub-triggers under the same base trigger; the import validator rejects it.

## Trigger catalog
`;

export function buildAutomationPrompt(): string {
  const triggers = flattenTriggers(AUTOMATION_TRIGGER_OPTIONS)
    .map((t) => formatTrigger(t, TRIGGER_CHAIN.get(t.id)))
    .join("\n\n");
  const actions = flattenActions(AUTOMATION_ACTION_OPTIONS)
    .map((a) => formatAction(a, ACTION_CHAIN.get(a.id)))
    .join("\n\n");
  return `${HEADER}\n${triggers}\n\n## Action catalog\n\n${actions}\n`;
}
